import React from 'react';
import { gql } from '@apollo/client';
import { ChildProps } from '@apollo/client/react/hoc';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  CoreMyListingsSellerListingCards,
  reverb_search_SearchResponse,
} from '@reverbdotcom/commons/src/rql';

import LoadingOverlay from '../../shared/loading_overlay';
import PlaceholderSellerListingCard from './PlaceholderSellerListingCard';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';
import SellerListingCardContainer from './SellerListingCardContainer';
import SellerListingCardsToolbar from './SellerListingCardsToolbar';
import BulkSelectContext from './BulkSelectContext';
import bulkSelectReducer, { ActionType } from './bulkSelectReducer';

const SHIPPING_REGION_CODES = [
  EVERYWHERE_CODE,
];

interface IExternalProps {
  listingIds: string[] | number[];
}

type IProps = ChildProps<IExternalProps, CoreMyListingsSellerListingCards.Query>;

export function SellerListingCardsCollection({ data, listingIds }: IProps) {
  const { loading, me = {} } = data;
  const { shop = {} } = me;
  const { listings: listingsResponse } = shop;
  const {
    listings = [],
  }: reverb_search_SearchResponse = listingsResponse ?? {};
  const placeholderListings = Array.from(Array(listingIds.length).keys());
  const shouldRenderPlaceholderListings = loading;
  const sortedListings = listingIds.map(id => listings.find(x => x.id === id)).filter(listing => listing !== undefined);

  const initialBulkSelectState = {
    selectedListings: [],
    selectableListings: [],
  };

  const [bulkSelectState, dispatch] = React.useReducer(bulkSelectReducer, initialBulkSelectState);

  React.useEffect(() => {
    if (loading) return;

    dispatch({
      type: ActionType.ADD_SELECTABLE_LISTINGS,
      payload: {
        selectableListings: listings,
      },
    });
  }, [loading]);

  return (
    <LoadingOverlay showOverlay={loading}>
      <BulkSelectContext.Provider
        value={{
          bulkSelectState,
          dispatch,
        }}
      >
        <SellerListingCardsToolbar />

        {shouldRenderPlaceholderListings ? placeholderListings.map(element => (
          <div key={`placeholderListing-${element}`} className="pb-4">
            <PlaceholderSellerListingCard />
          </div>
        )) : sortedListings.map(listing => (
          <div key={listing.id} className="pb-4">
            <SellerListingCardContainer
              defaultListing={listing}
            />
          </div>
        ))}
      </BulkSelectContext.Provider>
    </LoadingOverlay>
  );
}

export const SellerListingCardFragment = gql`
  fragment SellerListingCard on Listing {
    _id
    id
    sellerUuid
    title
    slug
    sku
    state
    inventory
    condition {
      displayName
    }
    bumpRate {
      rate
    }
    sellerPrice {
      amount
      amountCents
      symbol
      currency
      display
    }
    images(input: { transform: "card_square", count: 1 }) {
      source
    }
    counts {
      views
      offers
      watchers
    }
    publishedAt {
      seconds
    }
    hasInventory
    isAutoOfferEligible
    autoOffers {
      id
    }
    orders {
      nodes {
        _id
      }
    }
    shop {
      _id
      currency
      canCurateSales
    }
    exclusiveChannel
    relistable
    publishValidation {
      isValid
      errors {
        message
      }
    }
  }
`;

export const FETCH_LISTING_QUERY = gql`
  query Core_MyListings_FetchListing($id: String) {
    listing(input: { id: $id }) {
      _id
      ...SellerListingCard
    }
  }
  ${SellerListingCardFragment}
`;

const withQuery = withGraphql<IExternalProps, CoreMyListingsSellerListingCards.Query>(
  gql`
    query Core_MyListings_SellerListingCards($listingIds: [String], $shippingRegionCodes: [String]) {
      me {
        uuid
        shop {
          _id
          listings(input: { ids: $listingIds, shippingRegionCodes: $shippingRegionCodes }) {
            listings {
              _id
              ...SellerListingCard
            }
          }
        }
      }
    },
    ${SellerListingCardFragment}
  `,
  {
    options: ({ listingIds }) => {
      return {
        ssr: false,
        variables: {
          listingIds,
          shippingRegionCodes: SHIPPING_REGION_CODES,
        },
        errorPolicy: 'all',
      };
    },
  },
);

export default withQuery(SellerListingCardsCollection);
